import Label from '@common/components/Label';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { StyledTooltip } from './StyledTooltip';
import { InfoIcon } from '@/assets/icons';

type Props = {
  i18nkey: string;
  children: ReactNode;
  amount?: number; // when using a number, make sure the translation has {{amount}}
  alignLabel?: 'top' | 'center';
  skipFocus?: boolean;
  labelRatio?: number;
  infoI18nKey?: string;
};

const renderLabel = (
  t: TFunction,
  alignLabel: 'top' | 'center',
  i18nkey: string,
  labelRatio: number,
  amount?: number,
  infoI18nKey?: string,
) => (
  <Typography
    sx={{
      flex: `1 1 ${labelRatio}%`,
      mr: 1,
      fontSize: '10px',
      mt: alignLabel === 'top' ? '10px' : 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      minWidth: 0,
    }}
  >
    {t<string>(i18nkey, { amount })}

    {infoI18nKey && (
      <StyledTooltip placement='top' enterDelay={0} title={t<string>(infoI18nKey)}>
        <IconButton
          size='small'
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => e.stopPropagation()}
        >
          <InfoIcon />
        </IconButton>
      </StyledTooltip>
    )}
  </Typography>
);

const renderContent = (children: ReactNode, labelRatio: number) => (
  <Box sx={{ flex: `1 1 ${100 - labelRatio}%`, minWidth: 0, width: '100%' }}>{children}</Box>
);

export function PropertiesLabel({
  alignLabel = 'center',
  i18nkey,
  amount,
  children,
  skipFocus = false,
  labelRatio = 30,
  infoI18nKey,
}: Props) {
  const { t } = useTranslation();

  if (skipFocus) {
    return (
      <Stack
        sx={{
          width: '100%',
          flexDirection: 'row',
          alignItems: alignLabel === 'top' ? 'flex-start' : 'center',
        }}
      >
        {renderLabel(t, alignLabel, i18nkey, labelRatio, amount, infoI18nKey)}
        {renderContent(children, labelRatio)}
      </Stack>
    );
  }

  return (
    <Label
      sx={{
        display: 'flex',
        width: '100%',
        alignItems: alignLabel === 'top' ? 'flex-start' : 'center',
      }}
      label={renderLabel(t, alignLabel, i18nkey, labelRatio, amount, infoI18nKey)}
    >
      {renderContent(children, labelRatio)}
    </Label>
  );
}
